import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  orderService,
  bffUserService,
  bffConfigService,
  fiscalService,
  cashDrawerService,
  type FoodOrder,
  type FoodOrderEvent,
  type FoodOrderStatus,
  type FoodOrderType,
  type FoodPaymentStatus,
  type BFFUser,
  type CashDrawer,
  type ItemModifier,
} from '@resort-os/api-client';
import {
  useAuth,
  useFormatCurrency,
  useCurrency,
  useSnackbar,
  usePermission,
  OrderReceiptDownload,
  PaymentModal,
  DataTable,
  DataList,
  PageSection,
  StatCard,
  type PaymentIntentPayload,
  type PaymentSubmissionResult,
  type Column,
  PinAuthorizationField,
  usePinVerification,
} from '@resort-os/ui';
import DiscountSection from '../components/DiscountSection';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Paper,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  MergeType as MergeIcon,
  CallSplit as SplitIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  DoneAll as ServedIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  Restaurant as RestaurantIcon,
  DeliveryDining as DeliveryDiningIcon,
  Hotel as HotelIcon,
} from '@mui/icons-material';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';
const MUTED = '#74706B';
const INK = '#0C0C0B';
const SERIF = '"Playfair Display", Georgia, serif';
const SANS = '"DM Sans", system-ui, sans-serif';

const PAYMENT_STATUS_STYLES: Record<
  FoodPaymentStatus,
  { label: string; icon: JSX.Element; bg: string; color: string }
> = {
  pending: {
    label: 'Pending',
    icon: <PendingIcon sx={{ fontSize: 12 }} />,
    bg: '#E3F2FD',
    color: '#0D47A1',
  },
  partially_paid: {
    label: 'Partially Paid',
    icon: <PendingIcon sx={{ fontSize: 12 }} />,
    bg: '#FFF3E0',
    color: '#E65100',
  },
  paid: {
    label: 'Paid',
    icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
    bg: '#E8F5E9',
    color: '#2E7D32',
  },
  refunded: {
    label: 'Refunded',
    icon: <ReceiptIcon sx={{ fontSize: 12 }} />,
    bg: '#F3E5F5',
    color: '#6A1B9A',
  },
  failed: {
    label: 'Failed',
    icon: <CancelIcon sx={{ fontSize: 12 }} />,
    bg: '#FFEBEE',
    color: '#C62828',
  },
  cancelled: {
    label: 'Cancelled',
    icon: <CancelIcon sx={{ fontSize: 12 }} />,
    bg: '#FFEBEE',
    color: '#C62828',
  },
  charged_to_room: {
    label: 'Charged to Room',
    icon: <HotelIcon sx={{ fontSize: 12 }} />,
    bg: '#E8F5E9',
    color: '#2C3E2D',
  },
};

const HANDOFF_ROLE_LABELS: Record<string, string> = {
  waiter: 'Waiter',
  shift_supervisor: 'Shift supervisor',
  manager: 'Manager',
  admin: 'Admin',
};

function handoffChoiceLabel(server: { first_name: string; last_name: string; role: string }) {
  const name = `${server.first_name || ''} ${server.last_name || ''}`.trim();
  const role = HANDOFF_ROLE_LABELS[server.role] || server.role;
  return name ? `${name} · ${role}` : role;
}

const ORDER_TYPE_LABELS: Record<FoodOrderType, string> = {
  dine_in: 'Dine In',
  takeout: 'Takeout',
  delivery: 'Delivery',
};

function describeOrderEvent(event: FoodOrderEvent, nameOf: (id?: string | null) => string) {
  const when = new Date(event.created_at).toLocaleString();
  switch (event.event_type) {
    case 'created':
      return `Raised by ${nameOf(event.actor_id)} · ${when}`;
    case 'transferred':
      return `${nameOf(event.actor_id)} transferred it from ${nameOf(event.from_user_id)} to ${nameOf(event.to_user_id)} · ${when}`;
    case 'reassigned':
      return `${nameOf(event.actor_id)} reassigned it from ${nameOf(event.from_user_id)} to ${nameOf(event.to_user_id)} · ${when}`;
    case 'served':
      return `Served by ${nameOf(event.actor_id)} · ${when}`;
    case 'billed':
      return `Bill processed by ${nameOf(event.actor_id)} · ${when}`;
    default:
      return `${event.event_type} · ${when}`;
  }
}

function OrderResponsibility({
  order,
  events,
  servers,
  handoffUserId,
  onHandoffUserId,
  canTransfer,
  canReassign,
  pending,
  getUserName,
  onSubmit,
}: {
  order: FoodOrder;
  events: FoodOrderEvent[];
  servers: Array<{ id: string; first_name: string; last_name: string; role: string }>;
  handoffUserId: string;
  onHandoffUserId: (id: string) => void;
  canTransfer: boolean;
  canReassign: boolean;
  pending: boolean;
  getUserName: (id?: string | null) => string;
  onSubmit: (kind: 'transfer' | 'reassign') => void;
}) {
  const assigneeId = order.assigned_server || order.created_by;
  const choices = servers.filter(server => server.id !== assigneeId);
  const showHandoff = canTransfer || canReassign;

  return (
    <Box sx={{ px: 4, pb: 3 }}>
      <Box sx={{ p: 2, bgcolor: '#FAFAF8', border: BORDER }}>
        <Typography variant="caption" sx={{ color: '#9B958F', display: 'block', mb: 1 }}>
          Responsibility
        </Typography>
        {events.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#6B6560' }}>
            Raised by {getUserName(order.created_by)}. No later transfers are recorded.
          </Typography>
        ) : (
          events.map(event => (
            <Typography key={event.id} variant="body2" sx={{ color: '#2C3E2D', mb: 0.5 }}>
              {describeOrderEvent(event, getUserName)}
            </Typography>
          ))
        )}
        {showHandoff && choices.length === 0 && (
          <Typography variant="body2" sx={{ color: '#6B6560', mt: 1.5 }}>
            No one else is available to take this order.
          </Typography>
        )}
        {showHandoff && choices.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="handoff-waiter-label">Staff</InputLabel>
              <Select
                labelId="handoff-waiter-label"
                label="Staff"
                value={handoffUserId}
                onChange={event => onHandoffUserId(event.target.value)}
                sx={{ borderRadius: 0, bgcolor: '#FFF' }}
              >
                {choices.map(server => (
                  <MenuItem key={server.id} value={server.id}>
                    {handoffChoiceLabel(server)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              disabled={!handoffUserId || pending || choices.length === 0}
              onClick={() => onSubmit(canReassign ? 'reassign' : 'transfer')}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                border: BORDER,
                color: ACCENT,
                px: 2,
              }}
            >
              {canReassign ? 'Reassign' : 'Transfer'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function HistoryPage({ PageHeader }: { PageHeader: React.FC<{ title: string }> }) {
  const queryClient = useQueryClient();
  const formatCurrency = useFormatCurrency();
  const { currency } = useCurrency();
  const { showSnackbar } = useSnackbar();
  const { user: currentUser } = useAuth();
  const canViewAllOrders = usePermission('orders.transactions.view_all');
  const canViewAllStats = usePermission('orders.transactions.view_all_stats');
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const paymentMethodRef = useRef<string>('mpesa');

  const [searchQuery, setSearchQuery] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitItemIds, setSplitItemIds] = useState<string[]>([]);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => bffUserService.list({ limit: 100 }),
  });
  const users: BFFUser[] = usersData?.users || [];

  const { data: serversData } = useQuery({
    queryKey: ['order-servers'],
    queryFn: () => bffUserService.listServers(),
  });
  const servers = serversData?.users || [];

  const [selectedOrder, setSelectedOrder] = useState<FoodOrder | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [handoffUserId, setHandoffUserId] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isRefundMode, setIsRefundMode] = useState(false);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const {
    pin,
    setPin,
    verifying,
    error: pinError,
    resetPin,
    verifyPin,
    hasPin,
  } = usePinVerification();
  const [payDialogOpen, setPayDialogOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [actualCash, setActualCash] = useState('');

  const { data: currentDrawer, refetch: refetchDrawer } = useQuery<CashDrawer | null>({
    queryKey: ['cash-drawer-current'],
    queryFn: async () => {
      try {
        const res = await cashDrawerService.current();
        return res.data;
      } catch {
        return null;
      }
    },
    retry: false,
  });

  const openDrawerMutation = useMutation({
    mutationFn: (data: { opening_balance: number }) => cashDrawerService.open(data),
    onSuccess: () => {
      refetchDrawer();
      setDrawerOpen(false);
      setOpeningBalance('');
      showSnackbar('Drawer opened', 'success');
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to open drawer', 'error');
    },
  });

  const closeDrawerMutation = useMutation({
    mutationFn: (data: { actual_cash: number; expected_cash: number; closing_balance: number }) =>
      cashDrawerService.close(currentDrawer!.id, data),
    onSuccess: () => {
      refetchDrawer();
      setActualCash('');
      showSnackbar('Drawer closed', 'success');
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to close drawer', 'error');
    },
  });

  const { data: configsData } = useQuery({
    queryKey: ['configs'],
    queryFn: () => bffConfigService.getAll(),
  });

  const configs = (configsData as any)?.configs ?? [];
  const roomChargeEnabled = useMemo(() => {
    const c = configs.find((x: any) => x.key === 'feature.room_charge');
    return c ? c.value === 'true' || c.value === true : false;
  }, [configs]);

  const companyProfile = useMemo(() => {
    const read = (key: string) => {
      const entry = configs.find((x: any) => x.key === key);
      const value = entry?.value;
      if (typeof value === 'string') return value.trim();
      if (value && typeof value === 'object' && typeof value.value === 'string') {
        return value.value.trim();
      }
      return '';
    };
    return {
      name: read('app.company_name') || 'Resort',
      pin: read('app.kra_pin') || read('etims.pin'),
      serial: read('etims.device_id') || read('etims.register_number'),
    };
  }, [configs]);

  const { data: allOrdersData, isLoading } = useQuery({
    queryKey: ['orders', 'all'],
    queryFn: () => orderService.list({ page: 1, page_size: 200 }),
  });

  const isOwnOrder = (order: FoodOrder) =>
    !!currentUser?.id &&
    (order.created_by === currentUser.id || order.assigned_server === currentUser.id);

  const allOrders = (allOrdersData?.data?.data || []).filter((o: FoodOrder) =>
    canViewAllOrders ? true : isOwnOrder(o),
  );

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => orderService.cancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setCancelDialogOpen(false);
      setCancelReason('');
      resetPin();
      setSelectedOrder(null);
      setIsRefundMode(false);
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to cancel order', 'error');
    },
  });

  const refundMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => orderService.refund(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setCancelDialogOpen(false);
      setCancelReason('');
      resetPin();
      setSelectedOrder(null);
      setIsRefundMode(false);
      showSnackbar('Order refunded and cancelled successfully', 'success');
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to refund order', 'error');
    },
  });

  const splitMutation = useMutation({
    mutationFn: ({ orderId, itemIds }: { orderId: string; itemIds: string[] }) =>
      orderService.split(orderId, itemIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setSplitDialogOpen(false);
      setSplitItemIds([]);
      setSelectedOrder(null);
      showSnackbar('Items split into a new order successfully', 'success');
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to split order', 'error');
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ orderIds }: { orderIds: string[] }) => {
      return orderService.mergeOrders(orderIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setMergeDialogOpen(false);
      setSelectedOrders([]);
    },
    onError: error => {
      console.error('Failed to merge orders:', error);
      alert('Failed to merge orders. Please try again.');
    },
  });

  const userMap = useMemo(() => {
    const map: Record<string, { first_name?: string; last_name?: string; email?: string }> = {};
    users.forEach(user => {
      if (user.id) map[user.id] = user;
    });
    servers.forEach(user => {
      if (user.id && !map[user.id]) map[user.id] = user;
    });
    return map;
  }, [users, servers]);

  const { data: orderEventsResponse } = useQuery({
    queryKey: ['order-events', selectedOrder?.id],
    enabled: detailDialogOpen && !!selectedOrder?.id,
    queryFn: () => orderService.listEvents(selectedOrder!.id),
  });
  const orderEvents: FoodOrderEvent[] = orderEventsResponse?.data?.events || [];

  const nameIds = useMemo(() => {
    const ids = new Set<string>();
    const add = (id?: string | null) => {
      if (id) ids.add(id);
    };
    add(selectedOrder?.created_by);
    add(selectedOrder?.assigned_server);
    orderEvents.forEach(event => {
      add(event.actor_id);
      add(event.from_user_id);
      add(event.to_user_id);
    });
    return Array.from(ids).sort();
  }, [selectedOrder, orderEvents]);

  const { data: namesData } = useQuery({
    queryKey: ['user-names', nameIds.join(',')],
    enabled: nameIds.length > 0,
    queryFn: () => bffUserService.lookupNames(nameIds),
  });

  const getUserName = (userId?: string | null) => {
    if (!userId) return 'Unknown';
    const named = (namesData?.users || []).find(user => user.id === userId);
    const user = named || userMap[userId];
    if (!user) return 'Unknown';
    return (
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      ('email' in user ? user.email : '') ||
      'Unknown'
    );
  };

  const filteredOrders = useMemo(() => {
    const baseOrders = isAdmin ? allOrders : allOrders.filter(o => isOwnOrder(o));
    return baseOrders.filter(order => {
      const matchesSearch =
        searchQuery === '' ||
        order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.guest_name && order.guest_name.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = orderTypeFilter === 'all' || order.order_type === orderTypeFilter;
      const matchesUser = isAdmin && userFilter !== 'all' ? order.created_by === userFilter : true;
      const matchesDate =
        (!startDate || (order.created_at && order.created_at >= startDate)) &&
        (!endDate || (order.created_at && order.created_at <= endDate + 'T23:59:59'));

      return matchesSearch && matchesType && matchesUser && matchesDate;
    });
  }, [
    allOrders,
    searchQuery,
    orderTypeFilter,
    userFilter,
    isAdmin,
    currentUser?.id,
    startDate,
    endDate,
  ]);

  const stats = useMemo(() => {
    const baseOrders = canViewAllStats ? filteredOrders : filteredOrders.filter(o => isOwnOrder(o));
    const totalOrders = baseOrders.length;
    const revenue = baseOrders.reduce((sum, o) => {
      const bill = o.total_amount ?? 0;
      if (o.payment_status === 'paid' || o.payment_status === 'charged_to_room') return sum + bill;
      if (o.payment_status === 'refunded') return sum - bill;
      return sum;
    }, 0);
    const pendingPayments = baseOrders.filter(
      o => o.payment_status === 'pending' || o.payment_status === 'partially_paid',
    ).length;
    const cancelledOrders = baseOrders.filter(o => o.status === 'cancelled').length;

    let pendingItems = 0;
    let servedItems = 0;
    baseOrders.forEach(order => {
      order.items?.forEach(item => {
        if (item.status === 'pending' || item.status === 'preparing') pendingItems++;
        if (item.status === 'served') servedItems++;
      });
    });

    return { totalOrders, revenue, pendingPayments, cancelledOrders, pendingItems, servedItems };
  }, [filteredOrders, currentUser?.id]);

  const paymentSummary = useMemo(() => {
    if (!selectedOrder) return null;

    const activeItems = (selectedOrder.items || []).filter(item => item.status !== 'cancelled');
    const subtotal = activeItems.reduce((sum, item: any) => sum + item.total_price, 0);
    const taxAmount = selectedOrder.tax_amount || 0;
    const discountAmount = selectedOrder.discount_amount || 0;
    const serviceCharge = selectedOrder.service_charge || 0;
    const inclusiveTotal = subtotal - discountAmount + serviceCharge;
    const exclusiveTotal = subtotal + taxAmount - discountAmount + serviceCharge;
    const storedTotal = selectedOrder.total_amount;
    const vatIncluded =
      Math.abs(storedTotal - inclusiveTotal) <= Math.abs(storedTotal - exclusiveTotal);
    const amountDue = storedTotal;

    return {
      subtotal,
      taxAmount,
      discountAmount,
      serviceCharge,
      amountDue,
      currency: selectedOrder.currency || currency?.code || 'KES',
      receiptLines: [
        { label: vatIncluded ? 'Subtotal (incl. VAT)' : 'Subtotal', value: subtotal },
        { label: vatIncluded ? 'VAT included' : 'VAT', value: taxAmount },
        { label: 'Service charge', value: serviceCharge },
        { label: 'Discount', value: discountAmount },
      ],
    };
  }, [selectedOrder]);

  function handleViewOrder(order: FoodOrder) {
    setSelectedOrder(order);
    setHandoffUserId('');
    setDetailDialogOpen(true);
  }

  const handoffMutation = useMutation({
    mutationFn: ({
      id,
      toUserId,
      kind,
    }: {
      id: string;
      toUserId: string;
      kind: 'transfer' | 'reassign';
    }) =>
      kind === 'reassign'
        ? orderService.reassign(id, toUserId)
        : orderService.transfer(id, toUserId),
    onSuccess: (response, variables) => {
      const order = response.data;
      setSelectedOrder(order);
      setHandoffUserId('');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-events', order.id] });
      showSnackbar(
        variables.kind === 'reassign' ? 'Order reassigned' : 'Order transferred',
        'success',
      );
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.error || 'Failed to change the assigned waiter', 'error');
    },
  });

  function resetCancelDialogState() {
    setCancelReason('');
    resetPin();
    setShowAdminAuth(false);
    setIsRefundMode(false);
  }

  function handleCancelClick(order: FoodOrder) {
    resetCancelDialogState();
    setSelectedOrder(order);
    setCancelDialogOpen(true);
  }

  function handleRefundClick(order: FoodOrder) {
    resetCancelDialogState();
    setSelectedOrder(order);
    setIsRefundMode(true);
    setShowAdminAuth(true);
    setCancelDialogOpen(true);
  }

  async function handleCancelConfirm() {
    if (!selectedOrder || !cancelReason.trim()) return;

    if (isRefundMode) {
      if (!hasPin) return;
      const ok = await verifyPin();
      if (!ok) {
        showSnackbar('Invalid PIN', 'error');
        return;
      }
      refundMutation.mutate({ id: selectedOrder.id, reason: cancelReason });
      return;
    }

    if (isAdmin) {
      cancelMutation.mutate({ id: selectedOrder.id, reason: cancelReason });
    } else {
      if (!hasPin) return;
      const ok = await verifyPin();
      if (!ok) {
        showSnackbar('Invalid PIN', 'error');
        return;
      }
      cancelMutation.mutate({ id: selectedOrder.id, reason: cancelReason });
    }
  }

  function handlePayClick(order: FoodOrder) {
    setSelectedOrder(order);
    setPayDialogOpen(true);
  }

  async function submitFiscalPayment(
    payload: PaymentIntentPayload,
  ): Promise<PaymentSubmissionResult> {
    if (!selectedOrder || !paymentSummary) {
      throw new Error('Select an order before processing payment.');
    }

    paymentMethodRef.current = payload.paymentMethod;

    const totalAdjustments = payload.tipAmount + payload.serviceChargeAmount;
    const customerPhone =
      payload.paymentMethod === 'mpesa' || payload.paymentMethod === 'split'
        ? payload.mpesaPhone
        : undefined;

    const response = await fiscalService.submitPaymentIntent({
      amount: paymentSummary.amountDue,
      currency: currency?.code || 'KES',
      paymentMethod: payload.paymentMethod.toUpperCase() as 'MPESA' | 'CASH' | 'CARD' | 'SPLIT',
      phoneNumber: customerPhone,
      referenceId: selectedOrder.order_number,
      referenceType: 'order',
      tipAmount: totalAdjustments,
      taxAmount: paymentSummary.taxAmount,
      metadata: {
        order_id: selectedOrder.id,
        order_number: selectedOrder.order_number,
        guest_name: selectedOrder.guest_name || '',
        guest_phone: selectedOrder.guest_phone || '',
        item_count: (selectedOrder.items || []).length,
        cash_received: payload.cashReceived || 0,
        split_mpesa_amount: payload.splitMpesaAmount || 0,
        split_cash_amount: payload.splitCashAmount || 0,
        offline_mode: payload.offlineMode || false,
      },
    });

    queryClient.invalidateQueries({ queryKey: ['orders'] });

    return {
      transaction_id: response.data.transactionId,
      reference: selectedOrder.order_number,
      message: response.data.message,
      status: mapStatusToLegacy(response.data.status),
    };
  }

  async function verifyFiscalPayment(
    result: PaymentSubmissionResult | null,
  ): Promise<PaymentSubmissionResult> {
    const transactionId = result?.transaction_id;
    if (!transactionId) {
      throw new Error('Missing transaction ID.');
    }

    const response = await fiscalService.getPaymentIntent(transactionId);
    const data = response.data;

    return {
      transaction_id: data.transactionId,
      reference: result?.reference,
      message: data.message,
      status: mapStatusToLegacy(data.status),
    };
  }

  function mapStatusToLegacy(status: string): PaymentSubmissionResult['status'] {
    switch (status) {
      case 'COMPLETED':
        return 'completed';
      case 'FAILED':
      case 'CANCELLED':
        return 'failed';
      case 'PENDING_APPROVAL':
        return 'pending';
      default:
        return 'pending';
    }
  }

  function handleMergeOrders() {
    if (selectedOrders.length >= 2) {
      mergeMutation.mutate({ orderIds: selectedOrders });
    }
  }

  function toggleOrderSelection(orderId: string) {
    setSelectedOrders(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId],
    );
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function canCancel(status: FoodOrderStatus, paymentStatus: FoodPaymentStatus): boolean {
    return (
      !['completed', 'cancelled'].includes(status) &&
      !['paid', 'refunded', 'cancelled', 'charged_to_room'].includes(paymentStatus)
    );
  }

  function canRefund(paymentStatus: FoodPaymentStatus, status: string): boolean {
    return (
      (paymentStatus === 'paid' || paymentStatus === 'partially_paid') && status !== 'cancelled'
    );
  }

  function canPay(paymentStatus: FoodPaymentStatus, orderStatus: string): boolean {
    return (
      (paymentStatus === 'pending' || paymentStatus === 'partially_paid') &&
      orderStatus !== 'cancelled'
    );
  }

  function canChargeToRoom(order: FoodOrder): boolean {
    return (
      roomChargeEnabled &&
      !!order.booking_id &&
      (order.payment_status === 'pending' || order.payment_status === 'partially_paid') &&
      order.status !== 'cancelled'
    );
  }

  const chargeToRoomMutation = useMutation({
    mutationFn: (orderId: string) => orderService.chargeToRoom(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      showSnackbar('Order charged to room successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Failed to charge to room:', error);
      showSnackbar(error?.response?.data?.error || 'Failed to charge to room', 'error');
    },
  });

  function handleChargeToRoom(order: FoodOrder) {
    if (!order.booking_id) {
      showSnackbar('No room/booking linked to this order', 'warning');
      return;
    }
    chargeToRoomMutation.mutate(order.id);
  }

  const columns = useMemo(
    (): Column<FoodOrder>[] => [
      {
        id: '_select' as any,
        label: '',
        minWidth: 40,
        format: (_, row) => (
          <input
            type="checkbox"
            checked={selectedOrders.includes(row.id)}
            disabled={
              row.payment_status === 'paid' ||
              row.payment_status === 'charged_to_room' ||
              row.status === 'completed' ||
              row.status === 'cancelled'
            }
            onChange={() => toggleOrderSelection(row.id)}
          />
        ),
      },
      {
        id: 'order_number',
        label: 'Order #',
        format: (_, row) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {row.has_merged && <MergeIcon sx={{ fontSize: 12, color: '#ED6C02' }} />}
            {row.order_number}
          </Box>
        ),
      },
      {
        id: 'order_type' as any,
        label: 'Type',
        format: v => ORDER_TYPE_LABELS[v as FoodOrderType] || String(v),
      },
      {
        id: 'payment_status' as any,
        label: 'Payment',
        format: v => {
          const style = PAYMENT_STATUS_STYLES[v as FoodPaymentStatus];
          return style ? (
            <Chip
              icon={style.icon}
              label={style.label}
              size="small"
              sx={{
                borderRadius: 0,
                bgcolor: style.bg,
                color: style.color,
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-icon': { color: style.color, ml: 0.5 },
              }}
            />
          ) : (
            String(v)
          );
        },
      },
      {
        id: 'table',
        label: 'Table',
        format: (_, row) => row.table?.table_number || '-',
      },
      {
        id: 'guest_name' as any,
        label: 'Guest',
        format: v => String(v || '-'),
      },
      {
        id: 'progress' as any,
        label: 'Progress',
        minWidth: 120,
        format: (_, row) => {
          const items = row.items || [];
          const totalItems = items.length;
          const served = items.filter((i: any) => i.status === 'served').length;
          const completed = items.filter((i: any) => i.status === 'completed').length;
          const cancelled = items.filter((i: any) => i.status === 'cancelled').length;
          const active = totalItems - cancelled;
          const done = served + completed;
          const pct = active > 0 ? (done / active) * 100 : 0;
          const allDone = active > 0 && completed === active;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 3,
                    bgcolor: '#E2DDD8',
                    borderRadius: 0,
                    '& .MuiLinearProgress-bar': { bgcolor: allDone ? '#2E7D32' : ACCENT },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: allDone ? '#2E7D32' : '#6B6560',
                  minWidth: 35,
                  textAlign: 'right',
                  fontWeight: allDone ? 600 : 400,
                }}
              >
                {allDone ? 'Done ✓' : `${done}/${active}`}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: 'total_amount',
        label: 'Total',
        align: 'right',
        format: (v, row) => {
          const items = row.items || [];
          const total =
            items
              .filter((i: any) => i.status !== 'cancelled')
              .reduce((sum: number, i: any) => sum + i.total_price, 0) * 1.16;
          return (
            <span
              style={{
                fontWeight: 500,
                color:
                  row.status === 'cancelled'
                    ? '#C62828'
                    : row.payment_status === 'paid'
                      ? '#2E7D32'
                      : row.payment_status === 'pending'
                        ? '#0D47A1'
                        : 'inherit',
              }}
            >
              {formatCurrency(total)}
            </span>
          );
        },
      },
      {
        id: 'created_at' as any,
        label: 'Date',
        format: v => formatDate(String(v)),
      },
      {
        id: 'actions' as any,
        label: 'Actions',
        align: 'center',
        format: (_, row) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                data-tour="history-view-btn"
                onClick={() => handleViewOrder(row)}
                sx={{ color: '#6B6560' }}
              >
                <ViewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {canCancel(row.status, row.payment_status) && (
              <Tooltip title="Cancel Order">
                <IconButton
                  size="small"
                  onClick={() => handleCancelClick(row)}
                  sx={{ color: '#6B6560' }}
                >
                  <CancelIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {canChargeToRoom(row) && (
              <Tooltip title="Charge to Room">
                <IconButton
                  size="small"
                  onClick={() => handleChargeToRoom(row)}
                  sx={{ color: '#2C3E2D' }}
                >
                  <HotelIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {canPay(row.payment_status, row.status) && (
              <Tooltip title="Process Payment">
                <IconButton
                  size="small"
                  onClick={() => handlePayClick(row)}
                  sx={{ color: '#6B6560' }}
                >
                  <PaymentIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [selectedOrders, formatCurrency],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <PageSection>
          <PageHeader title="Order History" />

          <Box sx={{ flex: 1 }}>
            {selectedOrders.length > 0 && (
              <Paper
                data-tour="history-actions"
                elevation={0}
                sx={{
                  mb: 3,
                  p: { xs: 2, md: 2 },
                  bgcolor: '#FFF',
                  border: BORDER,
                  borderRadius: { xs: 3, md: 0 },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1.5, md: 0 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    color: '#6B6560',
                    fontWeight: { xs: 600, md: 400 },
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  {selectedOrders.length} order{selectedOrders.length > 1 ? 's' : ''} selected
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 1, md: 1.5 },
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setMergeDialogOpen(true)}
                    disabled={selectedOrders.length < 2}
                    startIcon={<MergeIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      color: selectedOrders.length < 2 ? '#BDBDBD' : '#6B6560',

                      fontSize: '0.7rem',
                      textTransform: 'none',
                      py: { xs: 1, md: 0.5 },
                      border: { xs: BORDER, md: 'none' },
                      borderRadius: { xs: 3, md: 0 },
                      justifyContent: 'center',
                      '&:hover': { bgcolor: selectedOrders.length < 2 ? 'transparent' : '#FAFAF8' },
                    }}
                  >
                    Merge Bills{selectedOrders.length < 2 ? ' (select 2+)' : ''}
                  </Button>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Divider orientation="vertical" flexItem />
                  </Box>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Divider />
                  </Box>
                  <Tooltip
                    title={
                      selectedOrders.length === 1
                        ? (() => {
                            const order = allOrders.find(o => o.id === selectedOrders[0]);
                            if (order?.payment_status === 'charged_to_room')
                              return 'Cannot split charged-to-room orders';
                            return 'Split items from this order';
                          })()
                        : 'Select one order to split'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        disabled={
                          selectedOrders.length !== 1 ||
                          (() => {
                            const order = allOrders.find(o => o.id === selectedOrders[0]);
                            return order?.payment_status === 'charged_to_room';
                          })()
                        }
                        startIcon={<SplitIcon sx={{ fontSize: 18 }} />}
                        onClick={() => {
                          const order = allOrders.find(o => o.id === selectedOrders[0]);
                          if (order) {
                            setSelectedOrder(order);
                            setSplitDialogOpen(true);
                            setSplitItemIds([]);
                          }
                        }}
                        sx={{
                          color: selectedOrders.length === 1 ? ACCENT : '#BDBDBD',

                          fontSize: '0.7rem',
                          textTransform: 'none',
                          py: { xs: 1, md: 0.5 },
                          border: { xs: BORDER, md: 'none' },
                          borderRadius: { xs: 3, md: 0 },
                          justifyContent: 'center',
                          width: { xs: '100%', md: 'auto' },
                        }}
                      >
                        Split Bill
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Divider orientation="vertical" flexItem />
                  </Box>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Divider />
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setSelectedOrders([])}
                    sx={{
                      color: '#6B6560',

                      fontSize: '0.7rem',
                      textTransform: 'none',
                      py: { xs: 1, md: 0.5 },
                      border: { xs: BORDER, md: 'none' },
                      borderRadius: { xs: 3, md: 0 },
                      justifyContent: 'center',
                      '&:hover': { bgcolor: '#FAFAF8' },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            )}

            <Box sx={{ display: 'flex', mb: '20px' }}>
              <Box sx={{ flex: 1 }}>
                <StatCard
                  label="Total Orders"
                  value={allOrders.length}
                  sub="All order types"
                  highlight
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatCard
                  label="Revenue"
                  value={formatCurrency(
                    allOrders.reduce((s, o) => {
                      const items = o.items || [];
                      return (
                        s +
                        items
                          .filter((i: any) => i.status !== 'cancelled')
                          .reduce((sum, i: any) => sum + i.total_price, 0) *
                          1.16
                      );
                    }, 0),
                  )}
                  sub="Net of refunds"
                  highlight
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatCard
                  label="Pending Items"
                  value={allOrders.reduce(
                    (s, o) =>
                      s +
                      (o.items || []).filter(
                        (i: any) =>
                          i.status !== 'served' &&
                          i.status !== 'completed' &&
                          i.status !== 'cancelled',
                      ).length,
                    0,
                  )}
                  sub="Awaiting kitchen"
                  warn
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatCard
                  label="Served"
                  value={allOrders.reduce(
                    (s, o) =>
                      s +
                      (o.items || []).filter(
                        (i: any) => i.status === 'served' || i.status === 'completed',
                      ).length,
                    0,
                  )}
                  sub="Delivered to guest"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatCard
                  label="Cancelled"
                  value={allOrders.filter(o => o.status === 'cancelled').length}
                  sub="Voided orders"
                  warn
                />
              </Box>
            </Box>

            <Box
              data-tour="history-filters"
              sx={{ display: 'flex', gap: { xs: 1, md: 2 }, mb: 3, flexWrap: 'wrap' }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6B6560',
                    mb: 0.5,
                    display: 'block',
                    letterSpacing: '0.06em',
                  }}
                >
                  Search
                </Typography>
                <TextField
                  placeholder="Order # or guest name"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  InputProps={{
                    sx: {
                      borderRadius: 0,

                      fontSize: '0.75rem',
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6B6560',
                    mb: 0.5,
                    display: 'block',
                    letterSpacing: '0.06em',
                  }}
                >
                  Order Type
                </Typography>
                <Select
                  size="small"
                  value={orderTypeFilter}
                  onChange={e => setOrderTypeFilter(e.target.value)}
                  sx={{
                    borderRadius: 0,

                    fontSize: '0.75rem',
                    minWidth: 140,
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="dine_in">Dine In</MenuItem>
                  <MenuItem value="takeout">Takeout</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                </Select>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6B6560',
                    mb: 0.5,
                    display: 'block',
                    letterSpacing: '0.06em',
                  }}
                >
                  From
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    sx: {
                      borderRadius: 0,

                      fontSize: '0.75rem',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6B6560',
                    mb: 0.5,
                    display: 'block',
                    letterSpacing: '0.06em',
                  }}
                >
                  To
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    sx: {
                      borderRadius: 0,

                      fontSize: '0.75rem',
                    },
                  }}
                />
              </Box>

              {isAdmin && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      mb: 0.5,
                      display: 'block',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Created By
                  </Typography>
                  <Select
                    size="small"
                    value={userFilter}
                    onChange={e => setUserFilter(e.target.value)}
                    sx={{
                      borderRadius: 0,

                      fontSize: '0.75rem',
                      minWidth: 180,
                    }}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    {users.map(user => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name || ''} {user.last_name || ''}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            </Box>

            {currentDrawer && currentDrawer.status === 'open' ? (
              <Paper
                elevation={0}
                sx={{
                  border: BORDER,
                  p: 1.5,
                  mb: 2,
                  bgcolor: '#FFF',
                  borderRadius: { xs: 3, md: 0 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',

                    fontWeight: 600,
                    color: '#2E7D32',
                  }}
                >
                  Drawer Open
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B6560' }}>
                  Float: {formatCurrency(currentDrawer.opening_balance)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B6560' }}>
                  Expected: {formatCurrency(currentDrawer.expected_cash ?? 0)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Actual cash"
                    value={actualCash}
                    onChange={e => setActualCash(e.target.value)}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }, width: 120 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!actualCash || closeDrawerMutation.isPending}
                    onClick={() => {
                      if (!currentDrawer) return;
                      closeDrawerMutation.mutate({
                        actual_cash: Number(actualCash),
                        expected_cash: currentDrawer.expected_cash ?? currentDrawer.opening_balance,
                        closing_balance: Number(actualCash),
                      });
                    }}
                    sx={{
                      bgcolor: '#C62828',
                      borderRadius: 0,
                      fontSize: '0.7rem',
                      textTransform: 'none',
                    }}
                  >
                    {closeDrawerMutation.isPending ? '...' : 'Close Drawer'}
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: BORDER,
                  p: 1.5,
                  mb: 2,
                  borderRadius: { xs: 3, md: 0 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',

                    fontWeight: 600,
                    color: '#6B6560',
                  }}
                >
                  Cash Drawer
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9B958F' }}>
                  No drawer open
                </Typography>
                {drawerOpen ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Float amount"
                      value={openingBalance}
                      onChange={e => setOpeningBalance(e.target.value)}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }, width: 120 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!openingBalance || openDrawerMutation.isPending}
                      onClick={() =>
                        openDrawerMutation.mutate({ opening_balance: Number(openingBalance) })
                      }
                      sx={{
                        bgcolor: ACCENT,
                        borderRadius: 0,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                      }}
                    >
                      {openDrawerMutation.isPending ? '...' : 'Open'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setDrawerOpen(false);
                        setOpeningBalance('');
                      }}
                      sx={{ fontSize: '0.7rem', color: '#9B958F', textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    sx={{
                      ml: 'auto',
                      color: ACCENT,
                      fontSize: '0.7rem',
                      textTransform: 'none',
                    }}
                    onClick={() => setDrawerOpen(true)}
                  >
                    + Open Drawer
                  </Button>
                )}
              </Paper>
            )}

            {filteredOrders.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  border: BORDER,
                  bgcolor: '#FFF',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#6B6560', mb: 1, fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  No orders found
                </Typography>
                <Typography variant="body2" sx={{ color: '#9C9C9C' }}>
                  {searchQuery || orderTypeFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Orders will appear here'}
                </Typography>
              </Box>
            ) : (
              <Box data-tour="history-table">
                <DataList
                  columns={[
                    {
                      id: '_select',
                      gridMin: '36px',
                      gridMax: '36px',
                      render: (row: FoodOrder) => (
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(row.id)}
                          disabled={
                            row.payment_status === 'paid' ||
                            row.payment_status === 'charged_to_room' ||
                            row.status === 'completed' ||
                            row.status === 'cancelled'
                          }
                          onChange={() => toggleOrderSelection(row.id)}
                          style={{ width: 16, height: 16 }}
                        />
                      ),
                    },
                    {
                      id: 'order_number',
                      gridMin: '160px',
                      gridMax: '210px',
                      render: (row: FoodOrder) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {row.has_merged && <MergeIcon sx={{ fontSize: 12, color: '#ED6C02' }} />}
                          <Typography
                            sx={{
                              fontSize: '0.78rem',
                              color: INK,
                              fontWeight: 500,
                            }}
                          >
                            {row.order_number}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      id: 'merge_split',
                      gridMin: '70px',
                      gridMax: '90px',
                      render: (row: FoodOrder) => (
                        <Box>
                          {row.has_merged ? (
                            <Chip
                              label="Merged"
                              size="small"
                              sx={{
                                borderRadius: 0,
                                bgcolor: '#FFF3E0',
                                color: '#ED6C02',
                                fontWeight: 600,

                                fontSize: '0.58rem',
                                height: 20,
                              }}
                            />
                          ) : (row as any).is_split ? (
                            <Chip
                              label="Split"
                              size="small"
                              sx={{
                                borderRadius: 0,
                                bgcolor: '#E3F2FD',
                                color: '#0D47A1',
                                fontWeight: 600,

                                fontSize: '0.58rem',
                                height: 20,
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.68rem', color: '#B8B3AD' }}>
                              —
                            </Typography>
                          )}
                        </Box>
                      ),
                    },
                    {
                      id: 'order_type',
                      gridMin: '80px',
                      gridMax: '100px',
                      render: (row: FoodOrder) => (
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                          {ORDER_TYPE_LABELS[row.order_type] || row.order_type}
                        </Typography>
                      ),
                    },
                    {
                      id: 'payment_status',
                      gridMin: '120px',
                      gridMax: '150px',
                      render: (row: FoodOrder) => {
                        const style = PAYMENT_STATUS_STYLES[row.payment_status];
                        return style ? (
                          <Chip
                            icon={style.icon}
                            label={style.label}
                            size="small"
                            sx={{
                              borderRadius: 0,
                              bgcolor: style.bg,
                              color: style.color,
                              fontWeight: 600,
                              fontSize: '0.6rem',
                              height: 22,
                              '& .MuiChip-icon': { color: style.color, ml: 0.5 },
                            }}
                          />
                        ) : (
                          <span>{row.payment_status}</span>
                        );
                      },
                    },
                    {
                      id: 'table',
                      gridMin: '60px',
                      gridMax: '80px',
                      render: (row: FoodOrder) => (
                        <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                          {row.table?.table_number || '—'}
                        </Typography>
                      ),
                    },
                    {
                      id: 'guest_name',
                      gridMin: '90px',
                      gridMax: '120px',
                      render: (row: FoodOrder) => (
                        <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                          {row.guest_name || '—'}
                        </Typography>
                      ),
                    },
                    {
                      id: 'progress',
                      gridMin: '120px',
                      gridMax: '160px',
                      render: (row: FoodOrder) => {
                        const items = row.items || [];
                        const totalItems = items.length;
                        const served = items.filter((i: any) => i.status === 'served').length;
                        const completed = items.filter((i: any) => i.status === 'completed').length;
                        const cancelled = items.filter((i: any) => i.status === 'cancelled').length;
                        const active = totalItems - cancelled;
                        const done = served + completed;
                        const pct = active > 0 ? (done / active) * 100 : 0;
                        const allDone = active > 0 && completed === active;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{
                                  height: 3,
                                  bgcolor: '#E2DDD8',
                                  borderRadius: 0,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: allDone ? '#2E7D32' : ACCENT,
                                  },
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: allDone ? '#2E7D32' : MUTED,
                                minWidth: 35,
                                textAlign: 'right',
                                fontWeight: allDone ? 600 : 400,
                              }}
                            >
                              {allDone ? 'Done ✓' : `${done}/${active}`}
                            </Typography>
                          </Box>
                        );
                      },
                    },
                    {
                      id: 'total_amount',
                      gridMin: '100px',
                      gridMax: '130px',
                      render: (row: FoodOrder) => {
                        const items = row.items || [];
                        const total =
                          items
                            .filter((i: any) => i.status !== 'cancelled')
                            .reduce((sum, i: any) => sum + i.total_price, 0) * 1.16;
                        return (
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              color:
                                row.status === 'cancelled'
                                  ? '#C62828'
                                  : row.payment_status === 'paid'
                                    ? '#2E7D32'
                                    : row.payment_status === 'pending'
                                      ? '#0D47A1'
                                      : INK,
                              textAlign: 'right',
                            }}
                          >
                            {formatCurrency(total)}
                          </Typography>
                        );
                      },
                    },
                    {
                      id: 'created_at',
                      gridMin: '100px',
                      gridMax: '130px',
                      render: (row: FoodOrder) => (
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                          {formatDate(row.created_at)}
                        </Typography>
                      ),
                    },
                  ]}
                  data={filteredOrders}
                  rowKey={row => row.id}
                  loading={isLoading}
                  emptyTitle="No orders found"
                  emptyDescription={
                    searchQuery || orderTypeFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Orders will appear here'
                  }
                  renderActions={(row: FoodOrder) => (
                    <>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleViewOrder(row);
                        }}
                        title="View"
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0,
                          border: '1px solid #E4DFD9',
                          color: MUTED,
                          bgcolor: '#F5F3F0',
                          '&:hover': { bgcolor: ACCENT, borderColor: ACCENT, color: '#FAFAF7' },
                        }}
                      >
                        <ViewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      {canCancel(row.status, row.payment_status) && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleCancelClick(row);
                          }}
                          title="Cancel"
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 0,
                            border: '1px solid #E4DFD9',
                            color: MUTED,
                            bgcolor: '#F5F3F0',
                            '&:hover': {
                              bgcolor: '#BF360C',
                              borderColor: '#BF360C',
                              color: '#FAFAF7',
                            },
                          }}
                        >
                          <CancelIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}

                      {canChargeToRoom(row) && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleChargeToRoom(row);
                          }}
                          title="Charge to Room"
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 0,
                            border: '1px solid #E4DFD9',
                            color: MUTED,
                            bgcolor: '#F5F3F0',
                            '&:hover': { bgcolor: ACCENT, borderColor: ACCENT, color: '#FAFAF7' },
                          }}
                        >
                          <HotelIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {canPay(row.payment_status, row.status) && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handlePayClick(row);
                          }}
                          title="Payment"
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 0,
                            border: '1px solid #E4DFD9',
                            color: MUTED,
                            bgcolor: '#F5F3F0',
                            '&:hover': {
                              bgcolor: '#0D47A1',
                              borderColor: '#0D47A1',
                              color: '#FAFAF7',
                            },
                          }}
                        >
                          <PaymentIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </>
                  )}
                  getAccentColor={(row: FoodOrder) =>
                    row.payment_status === 'pending'
                      ? '#0D47A1'
                      : row.payment_status === 'paid' || row.payment_status === 'charged_to_room'
                        ? ACCENT
                        : row.payment_status === 'cancelled'
                          ? '#BF360C'
                          : row.payment_status === 'refunded'
                            ? '#6A1B9A'
                            : ACCENT
                  }
                  groupBy={(row: FoodOrder) =>
                    new Date(row.created_at).toLocaleDateString('en-KE', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  }
                />
              </Box>
            )}
          </Box>
        </PageSection>
      </Box>

      {/* Order Details Modal */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            maxHeight: '85vh',
          },
        }}
      >
        {selectedOrder && (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: ACCENT,
                zIndex: 1,
              }}
            />

            <Box
              sx={{
                px: 4,
                pt: 4,
                pb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9B958F',

                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Order Details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: ACCENT,
                    fontSize: '1.4rem',
                  }}
                >
                  {selectedOrder.order_number}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {selectedOrder.status === 'cancelled' && (
                  <Chip
                    label="Cancelled"
                    size="small"
                    sx={{ bgcolor: '#FFEBEE', color: '#C62828', borderRadius: 0, fontWeight: 600 }}
                  />
                )}
                {selectedOrder.has_merged && (
                  <Chip
                    label="Merged"
                    size="small"
                    sx={{ bgcolor: '#FFF3E0', color: '#ED6C02', borderRadius: 0, fontWeight: 600 }}
                  />
                )}
                <IconButton
                  onClick={() => setDetailDialogOpen(false)}
                  size="small"
                  sx={{ color: '#9B958F' }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {/* Meta bar */}
              <Box sx={{ px: 4, pb: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9B958F' }}>
                    Type
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {ORDER_TYPE_LABELS[selectedOrder.order_type]}
                  </Typography>
                </Box>
                {selectedOrder.table && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#9B958F' }}>
                      Table
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedOrder.table.table_number}
                    </Typography>
                  </Box>
                )}
                {selectedOrder.guest_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#9B958F' }}>
                      Guest
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedOrder.guest_name}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9B958F' }}>
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(selectedOrder.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9B958F' }}>
                    Raised by
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getUserName(selectedOrder.created_by)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9B958F' }}>
                    Assigned to
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getUserName(selectedOrder.assigned_server || selectedOrder.created_by)}
                  </Typography>
                </Box>
              </Box>

              {/* Items Table */}
              <Box sx={{ mx: 4, my: 2, border: BORDER, bgcolor: '#FFF' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', bgcolor: '#F5F3F0', borderBottom: BORDER }}>
                  <Typography
                    sx={{
                      flex: 1,
                      px: 2,
                      py: 1,

                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#6B6560',
                      borderRight: BORDER,
                    }}
                  >
                    Item
                  </Typography>
                  <Typography
                    sx={{
                      width: 50,
                      px: 1,
                      py: 1,

                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#6B6560',
                      textAlign: 'center',
                      borderRight: BORDER,
                    }}
                  >
                    Qty
                  </Typography>
                  <Typography
                    sx={{
                      width: 115,
                      px: 1,
                      py: 1,

                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#6B6560',
                      textAlign: 'right',
                      borderRight: BORDER,
                    }}
                  >
                    Price
                  </Typography>
                  <Typography
                    sx={{
                      width: 130,
                      px: 1,
                      py: 1,

                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#6B6560',
                      textAlign: 'right',
                    }}
                  >
                    Total
                  </Typography>
                </Box>

                {/* Rows */}
                {(selectedOrder.items || []).map((item: any) => {
                  const isCancelled = item.status === 'cancelled';
                  const isServed = item.status === 'served';
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        borderBottom: '1px solid #F0EDE8',
                        opacity: isCancelled ? 0.4 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          px: 2,
                          py: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderRight: BORDER,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: isCancelled ? '#C62828' : isServed ? ACCENT : '#E2DDD8',
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              fontSize: '0.82rem',
                            }}
                          >
                            {item.product_name}
                          </Typography>
                          {item.preparation_notes && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#9B958F',
                                display: 'block',
                                fontSize: '0.65rem',
                              }}
                            >
                              {item.preparation_notes}
                            </Typography>
                          )}
                          {item.modifiers && item.modifiers.length > 0 && (
                            <Box sx={{ mt: 0.25 }}>
                              {item.modifiers.map((mod: ItemModifier, mi: number) => (
                                <Typography
                                  key={mi}
                                  variant="caption"
                                  sx={{
                                    color: '#6B6560',
                                    display: 'block',
                                    fontSize: '0.6rem',
                                  }}
                                >
                                  + {mod.name}
                                  {mod.price > 0 && (
                                    <Box
                                      component="span"
                                      sx={{ color: '#2C3E2D', fontWeight: 600, ml: 0.5 }}
                                    >
                                      +{mod.price}
                                    </Box>
                                  )}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {(item.prepared_at || item.served_at) && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              {item.prepared_at && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    border: '1px solid',
                                    borderColor: ACCENT,
                                    px: 1,
                                    py: 0.15,
                                  }}
                                >
                                  <RestaurantIcon sx={{ fontSize: 10, color: ACCENT }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: ACCENT,

                                      fontSize: '0.62rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    Prep{' '}
                                    {(() => {
                                      const m = Math.round(
                                        (new Date(item.prepared_at).getTime() -
                                          new Date(item.created_at).getTime()) /
                                          60000,
                                      );
                                      return `${m}m`;
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                              {item.served_at && item.prepared_at && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    border: '1px solid',
                                    borderColor: '#E65100',
                                    px: 1,
                                    py: 0.15,
                                  }}
                                >
                                  <DeliveryDiningIcon sx={{ fontSize: 10, color: '#E65100' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#E65100',

                                      fontSize: '0.62rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    Serve{' '}
                                    {(() => {
                                      const m = Math.round(
                                        (new Date(item.served_at).getTime() -
                                          new Date(item.prepared_at!).getTime()) /
                                          60000,
                                      );
                                      return `${m}m`;
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                              {item.served_at && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    border: '1px solid',
                                    borderColor: '#2E7D32',
                                    px: 1,
                                    py: 0.15,
                                  }}
                                >
                                  <CheckCircleIcon sx={{ fontSize: 10, color: '#2E7D32' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#2E7D32',

                                      fontSize: '0.62rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    Total{' '}
                                    {(() => {
                                      const m = Math.round(
                                        (new Date(item.served_at).getTime() -
                                          new Date(item.created_at).getTime()) /
                                          60000,
                                      );
                                      return `${m}m`;
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          width: 50,
                          px: 1,
                          py: 1,
                          textAlign: 'center',
                          borderRight: BORDER,
                          textDecoration: isCancelled ? 'line-through' : 'none',
                          fontSize: '0.82rem',
                        }}
                      >
                        {item.quantity}
                      </Typography>
                      <Typography
                        sx={{
                          width: 115,
                          px: 1,
                          py: 1,
                          textAlign: 'right',
                          borderRight: BORDER,
                          textDecoration: isCancelled ? 'line-through' : 'none',
                          fontSize: '0.82rem',
                        }}
                      >
                        {formatCurrency(item.unit_price)}
                      </Typography>
                      <Typography
                        sx={{
                          width: 130,
                          px: 1,
                          py: 1,
                          textAlign: 'right',
                          fontWeight: 500,
                          textDecoration: isCancelled ? 'line-through' : 'none',
                          fontSize: '0.82rem',
                        }}
                      >
                        {formatCurrency(item.total_price)}
                      </Typography>
                    </Box>
                  );
                })}

                {/* Totals */}
                {(() => {
                  const activeItems = (selectedOrder.items || []).filter(
                    (i: any) => i.status !== 'cancelled',
                  );
                  const subtotal = activeItems.reduce(
                    (sum: number, i: any) => sum + i.total_price,
                    0,
                  );
                  const tax =
                    selectedOrder.tax_amount ??
                    activeItems.reduce(
                      (sum: number, i: any) => sum + i.total_price * ((i.tax_rate ?? 0) / 100),
                      0,
                    );
                  const total = subtotal + tax - (selectedOrder.discount_amount || 0);
                  return (
                    <>
                      <Box sx={{ display: 'flex', borderBottom: '1px solid #F0EDE8' }}>
                        <Box
                          sx={{
                            flex: 1,
                            px: 2,
                            py: 0.75,

                            fontSize: '0.75rem',
                            color: '#6B6560',
                            borderRight: BORDER,
                          }}
                        >
                          Subtotal
                        </Box>
                        <Typography
                          sx={{
                            width: 130,
                            px: 1,
                            py: 0.75,
                            textAlign: 'right',
                            fontSize: '0.82rem',
                          }}
                        >
                          {formatCurrency(subtotal)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', borderBottom: '1px solid #F0EDE8' }}>
                        <Box
                          sx={{
                            flex: 1,
                            px: 2,
                            py: 0.75,

                            fontSize: '0.75rem',
                            color: '#6B6560',
                            borderRight: BORDER,
                          }}
                        >
                          Tax
                        </Box>
                        <Typography
                          sx={{
                            width: 130,
                            px: 1,
                            py: 0.75,
                            textAlign: 'right',
                            fontSize: '0.82rem',
                          }}
                        >
                          {formatCurrency(tax)}
                        </Typography>
                      </Box>
                      {selectedOrder.discount_amount > 0 && (
                        <Box sx={{ display: 'flex', borderBottom: '1px solid #F0EDE8' }}>
                          <Box
                            sx={{
                              flex: 1,
                              px: 2,
                              py: 0.75,

                              fontSize: '0.75rem',
                              color: '#6B6560',
                              borderRight: BORDER,
                            }}
                          >
                            Discount
                          </Box>
                          <Typography
                            sx={{
                              width: 130,
                              px: 1,
                              py: 0.75,
                              textAlign: 'right',
                              fontSize: '0.82rem',
                              color: ACCENT,
                            }}
                          >
                            -{formatCurrency(selectedOrder.discount_amount)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', bgcolor: '#F5F3F0' }}>
                        <Box
                          sx={{
                            flex: 1,
                            px: 2,
                            py: 1,

                            fontWeight: 600,
                            fontSize: '0.8rem',
                            borderRight: BORDER,
                          }}
                        >
                          Total
                        </Box>
                        <Typography
                          sx={{
                            width: 130,
                            px: 1,
                            py: 1,
                            textAlign: 'right',

                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: ACCENT,
                          }}
                        >
                          {formatCurrency(total)}
                        </Typography>
                      </Box>
                      {selectedOrder.paid_amount > 0 && (
                        <>
                          <Box sx={{ display: 'flex', borderBottom: '1px solid #F0EDE8' }}>
                            <Box
                              sx={{
                                flex: 1,
                                px: 2,
                                py: 0.75,

                                fontSize: '0.75rem',
                                color: '#6B6560',
                                borderRight: BORDER,
                              }}
                            >
                              Paid
                            </Box>
                            <Typography
                              sx={{
                                width: 130,
                                px: 1,
                                py: 0.75,
                                textAlign: 'right',
                                fontSize: '0.82rem',
                                color: '#2E7D32',
                              }}
                            >
                              {formatCurrency(selectedOrder.paid_amount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex' }}>
                            <Box
                              sx={{
                                flex: 1,
                                px: 2,
                                py: 0.75,

                                fontSize: '0.75rem',
                                color: '#6B6560',
                                borderRight: BORDER,
                              }}
                            >
                              Balance
                            </Box>
                            <Typography
                              sx={{
                                width: 130,
                                px: 1,
                                py: 0.75,
                                textAlign: 'right',
                                fontSize: '0.82rem',
                                color: '#C62828',
                              }}
                            >
                              {formatCurrency(total - selectedOrder.paid_amount)}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </>
                  );
                })()}
              </Box>

              {(selectedOrder.payment_status === 'pending' ||
                selectedOrder.payment_status === 'partially_paid') && (
                <Box sx={{ px: 4, pb: 2 }}>
                  <DiscountSection orderId={selectedOrder.id} />
                </Box>
              )}

              <OrderResponsibility
                order={selectedOrder}
                events={orderEvents}
                servers={servers}
                handoffUserId={handoffUserId}
                onHandoffUserId={setHandoffUserId}
                canTransfer={
                  selectedOrder.status !== 'cancelled' &&
                  selectedOrder.status !== 'completed' &&
                  !isAdmin &&
                  currentUser?.id === (selectedOrder.assigned_server || selectedOrder.created_by)
                }
                canReassign={
                  isAdmin &&
                  selectedOrder.status !== 'cancelled' &&
                  selectedOrder.status !== 'completed'
                }
                pending={handoffMutation.isPending}
                getUserName={getUserName}
                onSubmit={kind => {
                  if (!handoffUserId) return;
                  handoffMutation.mutate({
                    id: selectedOrder.id,
                    toUserId: handoffUserId,
                    kind,
                  });
                }}
              />

              {/* Footer info */}
              {(selectedOrder.cancellation_reason || selectedOrder.notes) && (
                <Box sx={{ px: 4, pb: 3 }}>
                  {selectedOrder.cancellation_reason && (
                    <Box sx={{ p: 2, bgcolor: '#FAFAF8', border: BORDER, mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#9B958F',

                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Cancellation Reason
                      </Typography>
                      <Typography variant="body2">{selectedOrder.cancellation_reason}</Typography>
                    </Box>
                  )}
                  {selectedOrder.notes && (
                    <Box sx={{ p: 2, bgcolor: '#FAFAF8', border: BORDER }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#9B958F',

                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Order Notes
                      </Typography>
                      <Typography variant="body2">{selectedOrder.notes}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 4, py: 2.5, borderTop: BORDER, gap: 1.5 }}>
              {selectedOrder.payment_status === 'paid' &&
                (() => {
                  const payment = (selectedOrder as any)?.metadata?.payment;
                  return (
                    <OrderReceiptDownload
                      order={
                        {
                          ...selectedOrder,
                          fiscal_reference: payment?.transaction_id || undefined,
                          payment_method: payment?.method || undefined,
                          paid_amount: payment?.amount || selectedOrder.paid_amount,
                        } as any
                      }
                      companyName={companyProfile.name}
                      companyPin={companyProfile.pin || undefined}
                      etimsSerial={companyProfile.serial || undefined}
                      label="Download Receipt"
                      currency={currency?.code || selectedOrder.currency || 'KES'}
                    />
                  );
                })()}
              <Button
                onClick={() => setDetailDialogOpen(false)}
                sx={{
                  border: BORDER,
                  borderRadius: 0,
                  color: '#6B6560',
                  px: 3,

                  textTransform: 'none',
                  '&:hover': { bgcolor: '#FAFAF8' },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { border: BORDER, borderRadius: 0 } }}
      >
        <DialogTitle
          sx={{
            borderBottom: BORDER,
            pb: 2,
            color: ACCENT,
          }}
        >
          Merge Bills
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              mt: 3,
              mb: 2,
              bgcolor: '#FFF3E0',
              border: '1px solid #ED6C02',
              borderRadius: 0,
            }}
          >
            <WarningIcon sx={{ color: '#ED6C02', fontSize: 20 }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#ED6C02',
              }}
            >
              This action is irreversible. Once merged, the original orders cannot be recovered.
            </Typography>
          </Box>
          <Typography
            sx={{
              mt: 1,
              mb: 2,

              fontSize: '0.8rem',
              color: '#6B6560',
            }}
          >
            Combine selected orders into a single bill
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            {selectedOrders.map(orderId => {
              const order = allOrders.find(o => o.id === orderId);
              return order ? (
                <Paper
                  key={orderId}
                  sx={{ p: 2, border: BORDER, bgcolor: '#FAFAF8', borderRadius: 0 }}
                >
                  <Typography sx={{ fontWeight: 500 }}>Order #{order.order_number}</Typography>
                  <Typography variant="caption" sx={{ color: '#9B958F' }}>
                    {order.items?.length} items •{' '}
                    {formatCurrency(
                      order.items?.reduce((s, i) => s + i.total_price, 0) * 1.16 || 0,
                    )}
                  </Typography>
                </Paper>
              ) : null;
            })}
          </Box>
          <Typography variant="body2" sx={{ color: '#9B958F', fontStyle: 'italic' }}>
            All items will be combined. Original orders will be marked as merged.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: BORDER, gap: 1 }}>
          <Button
            onClick={() => setMergeDialogOpen(false)}
            sx={{ borderRadius: 0, border: BORDER, color: '#6B6560', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMergeOrders}
            disabled={mergeMutation.isPending}
            variant="contained"
            sx={{ bgcolor: ACCENT, borderRadius: 0, px: 3, '&:hover': { bgcolor: '#1a2d1b' } }}
          >
            {mergeMutation.isPending ? 'Merging...' : 'Confirm Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Dialog */}
      <Dialog
        open={splitDialogOpen}
        onClose={() => {
          setSplitDialogOpen(false);
          setSplitItemIds([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { border: BORDER, borderRadius: 0 } }}
      >
        <DialogTitle
          sx={{
            borderBottom: BORDER,
            pb: 2,
            color: ACCENT,
          }}
        >
          Split Order #{selectedOrder?.order_number}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            sx={{
              mt: 3,
              mb: 2,

              fontSize: '0.8rem',
              color: '#6B6560',
            }}
          >
            Select items to move to a new order. The original order will keep unselected items and
            its total will be recalculated.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(selectedOrder?.items || [])
              .filter((item: any) => item.status !== 'cancelled')
              .map((item: any) => {
                const isSelected = splitItemIds.includes(item.id);
                return (
                  <Paper
                    key={item.id}
                    onClick={() => {
                      setSplitItemIds(prev =>
                        isSelected ? prev.filter(id => id !== item.id) : [...prev, item.id],
                      );
                    }}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${ACCENT}` : BORDER,
                      borderRadius: 0,
                      bgcolor: isSelected ? '#F0F7F0' : '#FAFAF8',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': { bgcolor: '#F5F5F0' },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 500,

                          fontSize: '0.78rem',
                        }}
                      >
                        {item.quantity}× {item.product_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9B958F' }}>
                        {item.preparation_notes || 'No notes'} · {formatCurrency(item.total_price)}
                      </Typography>
                      {item.modifiers && item.modifiers.length > 0 && (
                        <Box sx={{ mt: 0.25 }}>
                          {item.modifiers.map((mod: ItemModifier, mi: number) => (
                            <Typography
                              key={mi}
                              variant="caption"
                              sx={{ color: '#6B6560', display: 'block', fontSize: '0.6rem' }}
                            >
                              + {mod.name}
                              {mod.price > 0 && (
                                <Box
                                  component="span"
                                  sx={{ color: '#2C3E2D', fontWeight: 600, ml: 0.5 }}
                                >
                                  +{mod.price}
                                </Box>
                              )}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: `2px solid ${isSelected ? ACCENT : '#BDBDBD'}`,
                        bgcolor: isSelected ? ACCENT : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: 'white' }} />}
                    </Box>
                  </Paper>
                );
              })}
          </Box>
          {splitItemIds.length > 0 && (
            <Typography variant="caption" sx={{ color: ACCENT, mt: 1, display: 'block' }}>
              {splitItemIds.length} item{splitItemIds.length !== 1 ? 's' : ''} selected — will be
              moved to a new order
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: BORDER, gap: 1 }}>
          <Button
            onClick={() => {
              setSplitDialogOpen(false);
              setSplitItemIds([]);
            }}
            sx={{ borderRadius: 0, border: BORDER, color: '#6B6560', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedOrder && splitItemIds.length > 0) {
                splitMutation.mutate({ orderId: selectedOrder.id, itemIds: splitItemIds });
              }
            }}
            disabled={splitItemIds.length === 0 || splitMutation.isPending}
            variant="contained"
            sx={{ bgcolor: ACCENT, borderRadius: 0, px: 3, '&:hover': { bgcolor: '#1a2d1b' } }}
          >
            {splitMutation.isPending
              ? 'Splitting...'
              : `Split ${splitItemIds.length} Item${splitItemIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          resetCancelDialogState();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { border: BORDER, borderRadius: 0 } }}
      >
        <DialogTitle
          sx={{
            borderBottom: BORDER,
            pb: 2,
            color: ACCENT,
          }}
        >
          Cancel Order{isRefundMode ? ' & Process Refund' : ''}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            sx={{
              mb: 2,
              mt: 3,

              fontSize: '0.8rem',
              color: '#6B6560',
            }}
          >
            {isRefundMode
              ? `This will refund and cancel order ${selectedOrder?.order_number}. Inventory stock will be restored.`
              : `Are you sure you want to cancel order ${selectedOrder?.order_number}?`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={isRefundMode ? 'Reason for refund' : 'Reason for cancellation'}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 }, mb: 2 }}
          />
          {(!isAdmin || isRefundMode) && (
            <PinAuthorizationField
              value={pin}
              onChange={setPin}
              expanded={showAdminAuth}
              onExpandedChange={setShowAdminAuth}
              disabled={verifying || cancelMutation.isPending || refundMutation.isPending}
              error={Boolean(pinError)}
              helperText={pinError ?? undefined}
              collapsible={!isRefundMode}
              autoFocus={isRefundMode}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: BORDER, gap: 1 }}>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              resetCancelDialogState();
            }}
            sx={{ borderRadius: 0, border: BORDER, color: '#6B6560', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={
              !cancelReason.trim() ||
              cancelMutation.isPending ||
              refundMutation.isPending ||
              verifying ||
              ((!isAdmin || isRefundMode) && !hasPin)
            }
            variant="contained"
            sx={{
              bgcolor: isRefundMode ? '#D32F2F' : ACCENT,
              borderRadius: 0,
              px: 3,
              '&:hover': { bgcolor: isRefundMode ? '#B71C1C' : '#1a2d1b' },
            }}
          >
            {cancelMutation.isPending || refundMutation.isPending || verifying
              ? isRefundMode
                ? 'Refunding...'
                : 'Cancelling...'
              : isRefundMode
                ? 'Confirm Refund'
                : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedOrder && paymentSummary && (
        <PaymentModal
          open={payDialogOpen}
          onClose={() => {
            setPayDialogOpen(false);
            setSelectedOrder(null);
          }}
          amount={paymentSummary.amountDue}
          currency={paymentSummary.currency}
          referenceId={selectedOrder.order_number}
          context="POS History"
          receiptLabel="Order summary"
          receiptLines={paymentSummary.receiptLines}
          onSubmit={submitFiscalPayment}
          onVerify={verifyFiscalPayment}
          onSuccess={async result => {
            if (!selectedOrder || !result) return;
            try {
              await orderService.markAsPaid(selectedOrder.id, {
                amount: paymentSummary?.amountDue ?? selectedOrder.total_amount,
                method: paymentMethodRef.current || 'cash',
                transactionId: result.transaction_id,
                phone: undefined,
              });
              queryClient.invalidateQueries({ queryKey: ['orders'] });
              showSnackbar('Payment processed successfully', 'success');
            } catch (err) {
              console.error('Failed to mark order as paid:', err);
              showSnackbar('Failed to update order payment status', 'error');
            }
          }}
        />
      )}
    </Box>
  );
}

export default HistoryPage;
